
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { CommunityMember } from "@/types";
import { StaffVisitDialog } from "./StaffVisitDialog";
import { Skeleton } from "@/components/ui/skeleton";

interface Visit {
  id: string;
  complaint: string;
  diagnosis: string;
  treatment: string;
  temperature: number;
  date: string;
  community_member?: {
    id: string;
    name: string;
    category: string;
  };
}

interface EmployeeVisitsListProps {
  searchTerm?: string;
}

export function EmployeeVisitsList({ searchTerm = "" }: EmployeeVisitsListProps) {
  const { user } = useAuth();
  const [selectedStaff, setSelectedStaff] = useState<CommunityMember | null>(null);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  
  // Get start and end of today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const { data: visits = [], isLoading } = useQuery({
    queryKey: ['daily-staff-visits', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return [];
      
      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          complaint,
          diagnosis,
          treatment,
          temperature,
          date,
          community_member:community_member_id (
            id,
            name,
            category
          )
        `)
        .eq('school_id', user.schoolId)
        .gte('date', today.toISOString())
        .lt('date', tomorrow.toISOString())
        .not('community_member_id', 'is', null);

      if (error) throw error;
      return data as Visit[];
    },
    enabled: !!user?.schoolId
  });

  const filteredVisits = visits.filter(visit => {
    const staffName = visit.community_member?.name.toLowerCase() || '';
    return staffName.includes(searchTerm.toLowerCase());
  });

  const openTreatDialog = (staff: CommunityMember) => {
    setSelectedStaff(staff);
    setStaffDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-40" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="border rounded-lg p-6 space-y-4">
              <Skeleton className="h-6 w-20" />
              <div className="space-y-2">
                <div className="grid grid-cols-[auto,1fr] gap-x-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="flex justify-end">
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          Today's Staff Visits ({visits.length})
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredVisits.map((visit) => (
          <Card key={visit.id}>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="text-xl font-bold">
                  {format(new Date(visit.date), 'HH:mm')}
                </div>
                
                <div className="space-y-2">
                  <div className="grid grid-cols-[auto,1fr] gap-x-2">
                    <span className="font-medium">Staff:</span>
                    <span>{visit.community_member?.name}</span>
                    
                    <span className="font-medium">Department:</span>
                    <span>{visit.community_member?.category.replace(/_/g, ' ')}</span>
                    
                    <span className="font-medium">Temperature:</span>
                    <span>{visit.temperature}°C</span>
                    
                    <span className="font-medium">Complaint:</span>
                    <span>{visit.complaint}</span>
                    
                    <span className="font-medium">Diagnosis:</span>
                    <span>{visit.diagnosis}</span>
                    
                    <span className="font-medium">Treatment:</span>
                    <span>{visit.treatment}</span>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => visit.community_member && openTreatDialog({
                      id: visit.community_member.id,
                      name: visit.community_member.name,
                      category: visit.community_member.category as "ADMIN" | "NON_TEACHING_STAFF" | "TEACHING_STAFF",
                      school_id: user?.schoolId || '',
                      created_at: '',
                      updated_at: ''
                    })}
                  >
                    Treat Again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <StaffVisitDialog
        staff={selectedStaff}
        open={staffDialogOpen}
        onOpenChange={setStaffDialogOpen}
      />
    </div>
  );
}
